import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  ApiError,
  CHAINS,
  getBlockHashAtHeight,
  getDifficultyAdjustment,
  getHashrate,
  getMempool,
  getRecentBlocks,
  getRecommendedFees,
  type ChainId,
  type ForkStatus,
} from '@/lib/mempool';

/**
 * Polling cadence. The mempool.space API (and forks of it) rate-limit
 * aggressively, so every endpoint is polled slowly and slow-moving data
 * (hashrate history, fork comparison) is polled even more slowly.
 */
const POLL_STANDARD_MS = 60_000; // fast-moving chain data
const POLL_SLOW_MS = 5 * 60_000; // slow-moving data

/** Exponential backoff, doubled for explicit 429 responses. */
function retryDelay(attempt: number, error: unknown): number {
  const base = error instanceof ApiError && error.isRateLimited ? 10_000 : 2_000;
  return Math.min(base * 2 ** attempt, 60_000);
}

function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status !== undefined) {
    // Retry throttling and server errors; give up on other 4xx.
    if (error.isRateLimited || error.status >= 500) return failureCount < 3;
    return false;
  }
  return failureCount < 3;
}

const retry = { retry: shouldRetry, retryDelay } as const;

/**
 * Latest blocks (newest first). The tip block doubles as the chain's
 * height / tip-hash source, so no separate tip-height endpoint is polled.
 */
export function useRecentBlocks(chainId: ChainId) {
  const chain = CHAINS[chainId];
  return useQuery({
    queryKey: ['mempool', chainId, 'blocks'],
    queryFn: ({ signal }) => getRecentBlocks(chain.apiBase, signal),
    staleTime: POLL_STANDARD_MS / 2,
    refetchInterval: POLL_STANDARD_MS,
    ...retry,
  });
}

export function useDifficultyAdjustment(chainId: ChainId) {
  const chain = CHAINS[chainId];
  return useQuery({
    queryKey: ['mempool', chainId, 'difficulty'],
    queryFn: ({ signal }) => getDifficultyAdjustment(chain.apiBase, signal),
    staleTime: POLL_STANDARD_MS / 2,
    refetchInterval: POLL_STANDARD_MS,
    ...retry,
  });
}

/** ~30 days of daily average hashrate plus the current estimate. */
export function useHashrate(chainId: ChainId) {
  const chain = CHAINS[chainId];
  return useQuery({
    queryKey: ['mempool', chainId, 'hashrate'],
    queryFn: ({ signal }) => getHashrate(chain.apiBase, signal),
    staleTime: POLL_SLOW_MS / 2,
    refetchInterval: POLL_SLOW_MS,
    ...retry,
  });
}

export function useMempool(chainId: ChainId) {
  const chain = CHAINS[chainId];
  return useQuery({
    queryKey: ['mempool', chainId, 'mempool'],
    queryFn: ({ signal }) => getMempool(chain.apiBase, signal),
    staleTime: POLL_STANDARD_MS / 2,
    refetchInterval: POLL_STANDARD_MS,
    ...retry,
  });
}

export function useRecommendedFees(chainId: ChainId) {
  const chain = CHAINS[chainId];
  return useQuery({
    queryKey: ['mempool', chainId, 'fees'],
    queryFn: ({ signal }) => getRecommendedFees(chain.apiBase, signal),
    staleTime: POLL_STANDARD_MS / 2,
    refetchInterval: POLL_STANDARD_MS,
    ...retry,
  });
}

/**
 * Fork check: compares the block hash both chains report at the lower of
 * the two tip heights. If the hashes differ, the chains have diverged at
 * or below that height. Only two extra requests per cycle.
 */
export function useForkStatus(standardHeight?: number, bip110Height?: number) {
  const comparedHeight =
    standardHeight !== undefined && bip110Height !== undefined
      ? Math.min(standardHeight, bip110Height)
      : undefined;

  return useQuery<ForkStatus>({
    queryKey: ['mempool', 'fork-status', comparedHeight],
    enabled: comparedHeight !== undefined,
    queryFn: async ({ signal }) => {
      if (comparedHeight === undefined) {
        throw new Error('Fork check requires both chain tips');
      }
      const height = comparedHeight;
      const [standardHash, bip110Hash] = await Promise.all([
        getBlockHashAtHeight(CHAINS.standard.apiBase, height, signal),
        getBlockHashAtHeight(CHAINS.bip110.apiBase, height, signal),
      ]);
      return {
        comparedHeight: height,
        standardHash: standardHash.trim(),
        bip110Hash: bip110Hash.trim(),
        match: standardHash.trim() === bip110Hash.trim(),
      };
    },
    staleTime: POLL_SLOW_MS / 2,
    refetchInterval: POLL_SLOW_MS,
    ...retry,
  });
}

/** Ticking clock for live "x seconds ago" displays. */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);

  return now;
}
